/**
 * Filter Service
 *
 * This service handles client-side filtering of referral data dynamically based on sitecoreConfig.filterConfig.
 * All filters are processed based on the FilterConfiguration from Sitecore, making it fully dynamic.
 *
 * Key Features:
 * - Only processes filters defined in sitecoreConfig.filterConfig
 * - Automatically handles all filter types: text, dropdown, date, dateRange, slider, checkbox
 * - Maps filter field IDs to ReferralData properties dynamically
 * - Handles special value mappings (e.g., leadType: "hot" -> "HotLead")
 *
 * To migrate to API filtering:
 * 1. Create an API version of this service that makes HTTP requests
 * 2. Update the useFilteredData hook to use the API service instead
 * 3. The interface remains the same, so no component changes needed
 */

import { ReferralData } from '../types';
import { FilterValues } from '../ActiveFilters';
import {
  FilterConfiguration,
  FilterGroup,
  FilterField,
  CheckboxGroup,
  SliderFieldConfig,
} from '../types';
import { getNestedProperty } from '../utils/filterUtils';

export interface FilterService {
  filterData(
    data: ReferralData[],
    filters: FilterValues,
    filterConfig?: FilterConfiguration
  ): ReferralData[];
}

/**
 * Gets the ReferralData property value using field mapping from Sitecore config
 * Supports both direct properties and nested paths with dot notation
 *
 * @param item The ReferralData item
 * @param fieldId The field identifier
 * @param fieldMapping Optional field mapping from configuration (supports dot notation for nested properties)
 * @returns The property value
 *
 * @example
 * // Direct property
 * getDataProperty(item, 'email', 'email') // returns item.email
 *
 * // Nested property
 * getDataProperty(item, 'utmCampaign', 'trackingParameter.utmCampaign') // returns item.trackingParameter.utmCampaign
 * getDataProperty(item, 'caregiverEmail', 'caregiver.email') // returns item.caregiver.email
 */
const getDataProperty = (item: ReferralData, fieldId: string, fieldMapping?: string): any => {
  // Use field mapping from Sitecore config if provided, otherwise use fieldId
  const path = fieldMapping || fieldId;

  // Use the utility function to handle both flat and nested properties
  return getNestedProperty(item, path);
};

/**
 * Client-side filter service implementation
 * Filters data in memory without API calls, dynamically based on filter configuration
 */
export class ClientSideFilterService implements FilterService {
  filterData(
    data: ReferralData[],
    filters: FilterValues,
    filterConfig?: FilterConfiguration
  ): ReferralData[] {
    const filtered = [...data];

    // Always use dynamic filters based on sitecoreConfig.filterConfig
    // If no config provided or disabled, return unfiltered data
    if (!filterConfig || !filterConfig.enabled) {
      return filtered;
    }

    // Apply dynamic filters based on sitecoreConfig.filterConfig
    // This ensures only filters defined in Sitecore configuration are processed
    return this.applyDynamicFilters(filtered, filters, filterConfig);
  }

  /**
   * Apply filters dynamically based on sitecoreConfig.filterConfig
   * Only processes filters that are defined in the FilterConfiguration
   */
  private applyDynamicFilters(
    data: ReferralData[],
    filters: FilterValues,
    filterConfig: FilterConfiguration
  ): ReferralData[] {
    let filtered = [...data];

    // Process each filter group from sitecoreConfig.filterConfig
    filterConfig.filterGroups.forEach((group: FilterGroup) => {
      if (!group.enabled) return;

      // Process regular fields
      if (group.fields) {
        group.fields.forEach((field: FilterField) => {
          if (!field.enabled) return;

          // For slider fields, check min/max fields instead of the main field
          if (field.type === 'slider') {
            const sliderField = field as SliderFieldConfig;
            const minField = sliderField.fields?.minField;
            const maxField = sliderField.fields?.maxField;
            const hasMinValue = minField && filters[minField];
            const hasMaxValue = maxField && filters[maxField];

            // Apply slider filter if either min or max value is set
            if (hasMinValue || hasMaxValue) {
              filtered = this.applyFieldFilter(filtered, field, '', filters);
            }
          } else {
            // For other field types, check the main field value
            const filterValue = filters[field.id];
            if (!filterValue) return;

            filtered = this.applyFieldFilter(filtered, field, filterValue, filters);
          }
        });
      }

      // Process checkbox groups
      if (group.checkboxGroups) {
        group.checkboxGroups.forEach((checkboxGroup: CheckboxGroup) => {
          if (!checkboxGroup.enabled) return;

          const fieldName = checkboxGroup.field || checkboxGroup.id;
          const filterValue = filters[fieldName];
          if (!filterValue) return;

          filtered = this.applyCheckboxGroupFilter(filtered, checkboxGroup, filterValue, fieldName);
        });
      }
    });

    return filtered;
  }

  /**
   * Apply filter for a single field based on its type
   */
  private applyFieldFilter(
    data: ReferralData[],
    field: FilterField,
    filterValue: string | string[],
    allFilters: FilterValues
  ): ReferralData[] {
    switch (field.type) {
      case 'text':
        // Text filters should always be strings
        return this.applyTextFilter(
          data,
          field,
          Array.isArray(filterValue) ? filterValue[0] : filterValue
        );

      case 'dropdown':
        return this.applyDropdownFilter(data, field, filterValue);

      case 'date':
        // Date filters should always be strings
        return this.applyDateFilter(
          data,
          field,
          Array.isArray(filterValue) ? filterValue[0] : filterValue
        );

      case 'dateRange':
        return this.applyDateRangeFilter(data, field, allFilters);

      case 'slider':
        return this.applySliderFilter(data, field as SliderFieldConfig, allFilters);

      default:
        return data;
    }
  }

  /**
   * Apply text filter (contains search)
   * Uses field mapping from Sitecore config
   */
  private applyTextFilter(data: ReferralData[], field: FilterField, value: string): ReferralData[] {
    if (!value) return data;

    const fieldMapping = field.field || field.id;

    return data.filter((item) => {
      const propertyValue = getDataProperty(item, field.id, fieldMapping);
      if (propertyValue === undefined || propertyValue === null) return false;

      // Handle special cases for protocol (can search in both)
      if (field.id === 'protocol' || fieldMapping === 'protocol') {
        return (
          item.associatedProtocols?.toLowerCase().includes(value.toLowerCase()) ||
          item.qualifiedProtocols?.toLowerCase().includes(value.toLowerCase())
        );
      }

      // Handle special cases for site (can search in multiple fields)
      if (field.id === 'site' || fieldMapping === 'site') {
        return (
          item.associatedProtocols?.toLowerCase().includes(value.toLowerCase()) ||
          item.qualifiedProtocols?.toLowerCase().includes(value.toLowerCase()) ||
          item.city?.toLowerCase().includes(value.toLowerCase()) ||
          item.state?.toLowerCase().includes(value.toLowerCase()) ||
          item.userIds?.toLowerCase().includes(value.toLowerCase())
        );
      }

      // Default: contains search
      const stringValue = String(propertyValue).toLowerCase();
      return stringValue.includes(value.toLowerCase());
    });
  }

  /**
   * Apply dropdown filter (exact match or multiple values)
   * Uses field mapping from Sitecore config
   * Supports both single and multi-select dropdowns
   */
  private applyDropdownFilter(
    data: ReferralData[],
    field: FilterField,
    value: string | string[]
  ): ReferralData[] {
    if (!value || (Array.isArray(value) && value.length === 0)) return data;

    const fieldMapping = field.field || field.id;

    // Handle array values (multi-select)
    if (Array.isArray(value)) {
      return data.filter((item) => {
        const propertyValue = getDataProperty(item, field.id, fieldMapping);
        if (propertyValue === undefined || propertyValue === null) return false;

        const propertyString = String(propertyValue).toLowerCase();

        // Check if property matches any of the selected values
        return value.some((val) => propertyString === val.toLowerCase());
      });
    }

    // Handle string values (single-select or comma-separated legacy format)
    const values = value.split(',').filter((v) => v.trim());
    const isMultipleValues = values.length > 1;

    return data.filter((item) => {
      const propertyValue = getDataProperty(item, field.id, fieldMapping);
      if (propertyValue === undefined || propertyValue === null) return false;

      const propertyString = String(propertyValue).toLowerCase();

      // For multiple values, check if property matches any of the selected values
      if (isMultipleValues) {
        return values.some((val) => propertyString === val.trim().toLowerCase());
      }

      // For single value, exact match
      return propertyString === value.toLowerCase();
    });
  }

  /**
   * Apply date filter (exact date match)
   * Uses field mapping from Sitecore config
   */
  private applyDateFilter(data: ReferralData[], field: FilterField, value: string): ReferralData[] {
    if (!value) return data;

    const fieldMapping = field.field || field.id;

    return data.filter((item) => {
      const propertyValue = getDataProperty(item, field.id, fieldMapping);
      if (!propertyValue) return false;

      try {
        const itemDate = new Date(propertyValue);
        const filterDate = new Date(value);
        return itemDate.toDateString() === filterDate.toDateString();
      } catch {
        return false;
      }
    });
  }

  /**
   * Apply date range filter.
   * Keys are scoped per filter: `${fieldId}From` / `${fieldId}To`.
   * The data property to compare is resolved via field.field, falling back to dateTimestamp.
   */
  private applyDateRangeFilter(
    data: ReferralData[],
    field: FilterField,
    filters: FilterValues
  ): ReferralData[] {
    let filtered = data;
    const fieldId = field.field || field.id;
    const fromKey = `${fieldId}From`;
    const toKey = `${fieldId}To`;

    const getItemDate = (item: ReferralData): Date => {
      // Try the field's own data mapping first; fall back to dateTimestamp
      const mapped = getDataProperty(item, field.id, field.field);
      const raw = mapped ?? item.dateTimestamp;
      return new Date(raw);
    };

    if (filters[fromKey]) {
      filtered = filtered.filter((item) => {
        const itemDate = getItemDate(item);
        const fromRaw = filters[fromKey];
        const fromValue = Array.isArray(fromRaw) ? fromRaw[0] : fromRaw;
        const fromDate = new Date(fromValue);
        fromDate.setHours(0, 0, 0, 0);
        itemDate.setHours(0, 0, 0, 0);
        return itemDate >= fromDate;
      });
    }

    if (filters[toKey]) {
      filtered = filtered.filter((item) => {
        const itemDate = getItemDate(item);
        const toRaw = filters[toKey];
        const toValue = Array.isArray(toRaw) ? toRaw[0] : toRaw;
        const toDate = new Date(toValue);
        toDate.setHours(23, 59, 59, 999);
        itemDate.setHours(0, 0, 0, 0);
        return itemDate <= toDate;
      });
    }

    return filtered;
  }

  /**
   * Apply slider filter (range filter)
   * Uses field mapping from Sitecore config
   * Handles percentage values (e.g., "85%") by removing the % sign
   */
  private applySliderFilter(
    data: ReferralData[],
    field: SliderFieldConfig,
    filters: FilterValues
  ): ReferralData[] {
    let filtered = data;

    const minField = field.fields?.minField;
    const maxField = field.fields?.maxField;
    const fieldMapping = field.field || field.id;

    // Helper to parse numeric value from string (handles percentage strings like "85%")
    const parseNumericValue = (value: string | number | undefined | null): number => {
      if (value === undefined || value === null) return NaN;
      const stringValue = String(value).replace('%', '').trim();
      return parseInt(stringValue) ?? NaN;
    };

    if (minField) {
      const minFilterValue = filters[minField];
      const minValueString = Array.isArray(minFilterValue) ? minFilterValue[0] : minFilterValue;
      const minValue = parseInt(minValueString || '');
      if (!isNaN(minValue)) {
        filtered = filtered.filter((item) => {
          const propertyValue = getDataProperty(item, field.id, fieldMapping);
          // Check for null/undefined specifically, not falsy (0 is a valid value)
          if (propertyValue === null || propertyValue === undefined) return false;
          const numValue = parseNumericValue(propertyValue);
          return !isNaN(numValue) && numValue >= minValue;
        });
      }
    }

    if (maxField) {
      const maxFilterValue = filters[maxField];
      const maxValueString = Array.isArray(maxFilterValue) ? maxFilterValue[0] : maxFilterValue;
      const maxValue = parseInt(maxValueString || '');
      if (!isNaN(maxValue)) {
        filtered = filtered.filter((item) => {
          const propertyValue = getDataProperty(item, field.id, fieldMapping);
          // Check for null/undefined specifically, not falsy (0 is a valid value)
          if (propertyValue === null || propertyValue === undefined) return false;
          const numValue = parseNumericValue(propertyValue);
          return !isNaN(numValue) && numValue <= maxValue;
        });
      }
    }

    return filtered;
  }

  /**
   * Apply checkbox group filter (comma-separated values or array)
   */
  private applyCheckboxGroupFilter(
    data: ReferralData[],
    checkboxGroup: CheckboxGroup,
    filterValue: string | string[],
    fieldName: string
  ): ReferralData[] {
    if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return data;

    // Handle both array and comma-separated string formats
    const selectedValues = Array.isArray(filterValue)
      ? filterValue
      : filterValue.split(',').filter((v) => v);

    if (selectedValues.length === 0) return data;

    // Use field mapping from checkboxGroup if available, otherwise use fieldName
    const fieldMapping = checkboxGroup.field || fieldName;

    return data.filter((item) => {
      // Get the actual property value from ReferralData using field mapping from config
      const propertyValue = getDataProperty(item, fieldName, fieldMapping);

      if (propertyValue === undefined || propertyValue === null) return false;

      // Special handling for leadType: map filter values to data values
      if (fieldName === 'leadType') {
        const valueMap: Record<string, string> = {
          hot: 'HotLead',
          warm: 'WarmLead',
          cold: 'ColdLead',
        };

        return selectedValues.some((filterVal) => {
          const normalizedFilter = filterVal.toLowerCase().trim();
          const mappedValue = valueMap[normalizedFilter] || normalizedFilter;
          return String(propertyValue).toLowerCase() === mappedValue.toLowerCase();
        });
      }

      // Special handling for status/dedupeStatus: map filter values to data values
      if (fieldName === 'status' || checkboxGroup.id === 'dedupeStatus') {
        const itemStatus = String(propertyValue).toLowerCase().trim();
        return selectedValues.some((filterStatus) => {
          const normalizedFilter = filterStatus.toLowerCase().trim();
          // Normalize both to compare (remove spaces, convert to lowercase, handle hyphens)
          const normalizedItemStatus = itemStatus.replace(/\s+/g, '-').toLowerCase();
          const normalizedFilterStatus = normalizedFilter.replace(/\s+/g, '-').toLowerCase();
          return normalizedItemStatus === normalizedFilterStatus;
        });
      }

      // Default: check if any selected value matches (case-insensitive)
      const stringValue = String(propertyValue).toLowerCase();
      return selectedValues.some((val) => stringValue === val.toLowerCase().trim());
    });
  }
}

// Export singleton instance
export const filterService: FilterService = new ClientSideFilterService();

/**
 * Future API-based filter service implementation example:
 *
 * export class ApiFilterService implements FilterService {
 *   async filterData(
 *     data: ReferralData[],
 *     filters: FilterValues,
 *     filterConfig?: FilterConfiguration
 *   ): Promise<ReferralData[]> {
 *     const response = await fetch('/api/referrals/filter', {
 *       method: 'POST',
 *       body: JSON.stringify({ filters, filterConfig }),
 *     });
 *     return response.json();
 *   }
 * }
 */
